import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText, 
  Alert, 
  CircularProgress, 
  Stepper, 
  Step, 
  StepLabel, 
  Card, 
  CardContent, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import { 
  LocalizationProvider, 
  DatePicker,
  TimePicker
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays, isAfter, isBefore, isEqual, addHours, isToday, addMonths } from 'date-fns';
import { SelectChangeEvent } from '@mui/material/Select';
import { useAuth } from '../contexts/AuthContext';
import { getVesselById, getVesselsByType } from '../firebase/services/vesselService';
import { createReservation, checkAvailability, getReservationsByDateRange } from '../firebase/services/reservationService';
import { Vessel, TimeSlot, MarinaSetting, PricingRule } from '../models/types';
import { Customer, CustomerInput } from '../firebase/services/customerVesselService';
import { formatPhoneNumber, formatTime12Hour } from '../utils/formatters';
import { useSettings } from '../contexts/SettingsContext';
import ReservationPayment from '../components/ReservationPayment';
import { getMarinaSettings } from '../firebase/services/marinaSettingsService';
import { validatePromoCode } from '../firebase/services/discountService';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Clear as ClearIcon } from '@mui/icons-material';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import MultiDayTimeSlotSelector from '../components/MultiDayTimeSlotSelector';
import CustomerSelector from '../components/CustomerSelector';
import { searchCustomers, createCustomer } from '../firebase/services/customerVesselService';
import { getPricingRules } from '../firebase/services/pricingService';

// HourlyTimeSlotSelector Component
interface HourlyTimeSlotSelectorProps {
  date: Date;
  vesselType: string;
  vessels: Vessel[];
  availableVessels: Vessel[];
  marinaSettings: MarinaSetting | null;
  onTimeSlotSelect: (timeSlot: TimeSlot, price?: number, duration?: number) => void;
  onBack: () => void;
}

const HourlyTimeSlotSelector: React.FC<HourlyTimeSlotSelectorProps> = ({
  date,
  vesselType,
  vessels,
  availableVessels,
  marinaSettings,
  onTimeSlotSelect,
  onBack
}) => {
  const [selectedStartTime, setSelectedStartTime] = useState<Date | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<string>('1');
  const [hourlySlots, setHourlySlots] = useState<{hour: number, available: boolean}[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businessHours, setBusinessHours] = useState<{openHour: number, closeHour: number}>({
    openHour: 9,
    closeHour: 17
  });
  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);
  
  // Initialize when component mounts
  useEffect(() => {
    const initializeTimeSlots = async () => {
      setLoading(true);
      
      try {
        // Get business hours for the selected day
        const dayOfWeek = format(date, 'EEEE').toLowerCase();
        
        // Default hours (only used if settings can't be loaded)
        let openTime = '09:00';
        let closeTime = '17:00';
        
        if (marinaSettings) {
          // Check if the date is in season (if seasonal hours are enabled)
          let isInSeason = true;
          if (marinaSettings.isSeasonalHoursEnabled && 
              marinaSettings.seasonStartDate && 
              marinaSettings.seasonEndDate) {
            
            // Parse season dates
            let seasonStart = null;
            let seasonEnd = null;
            
            try {
              // Use year from the selected date
              const yearToUse = date.getFullYear();
              
              // Parse season start date
              if (marinaSettings.seasonStartDate.includes('-')) {
                const parts = marinaSettings.seasonStartDate.split('-');
                if (parts.length === 2) {
                  // Format MM-DD, add year
                  const [month, day] = parts.map(Number);
                  seasonStart = new Date(yearToUse, month - 1, day);
                } else if (parts.length === 3) {
                  // Format YYYY-MM-DD
                  seasonStart = new Date(marinaSettings.seasonStartDate);
                  // Update year if needed
                  seasonStart.setFullYear(yearToUse);
                }
              }
              
              // Parse season end date
              if (marinaSettings.seasonEndDate.includes('-')) {
                const parts = marinaSettings.seasonEndDate.split('-');
                if (parts.length === 2) {
                  // Format MM-DD, add year
                  const [month, day] = parts.map(Number);
                  seasonEnd = new Date(yearToUse, month - 1, day);
                } else if (parts.length === 3) {
                  // Format YYYY-MM-DD
                  seasonEnd = new Date(marinaSettings.seasonEndDate);
                  // Update year if needed
                  seasonEnd.setFullYear(yearToUse);
                }
              }
              
              // Check if the selected date is within season
              if (seasonStart && seasonEnd) {
                isInSeason = date >= seasonStart && date <= seasonEnd;
              }
            } catch (err) {
              console.error('Error parsing seasonal dates:', err);
              // Default to in-season if there's an error
              isInSeason = true;
            }
          }
          
          // Get the appropriate business hours based on season
          const businessHours = isInSeason && marinaSettings.inSeasonHours 
            ? marinaSettings.inSeasonHours
            : (!isInSeason && marinaSettings.outOfSeasonHours 
              ? marinaSettings.outOfSeasonHours
              : marinaSettings.businessHours);
          
          // Get hours for the selected day of week
          if (businessHours && businessHours[dayOfWeek]) {
            const dayHours = businessHours[dayOfWeek];
            
            // Check if the marina is open on this day
            if (!dayHours.isClosed && dayHours.openTime && dayHours.closeTime) {
              openTime = dayHours.openTime;
              closeTime = dayHours.closeTime;
            } else if (dayHours.isClosed) {
              // If the marina is closed on this day, show an error
              setError(`The marina is closed on ${format(date, 'EEEE')}s.`);
              setHourlySlots([]);
              setLoading(false);
              return;
            }
          }
        }
        
        // Parse open and close hours
        const openHour = parseInt(openTime.split(':')[0]);
        const openMinute = parseInt(openTime.split(':')[1]);
        const closeHour = parseInt(closeTime.split(':')[0]);
        const closeMinute = parseInt(closeTime.split(':')[1]);
        
        // Store business hours for display
        setBusinessHours({ openHour, closeHour });
        
        // Generate hourly slots
        const slots = [];
        
        // Convert to minutes for more precise comparison
        const openMinutes = openHour * 60 + openMinute;
        const closeMinutes = closeHour * 60 + closeMinute;
        
        // Generate slots for each hour from opening to last valid starting time
        for (let hour = openHour; hour < closeHour; hour++) {
          // Calculate ending hour based on selected duration
          const endHour = hour + 1; // Minimum one hour rental
          
          // Check if a rental starting at this hour would end within business hours
          if (endHour <= closeHour) {
            // This is a valid starting time
            const isAvailable = await checkHourAvailability(hour);
            slots.push({ hour, available: isAvailable });
          }
        }
        
        setHourlySlots(slots);
        
        // Set default start time to the first available hour
        const firstAvailableSlot = slots.find(slot => slot.available);
        if (firstAvailableSlot) {
          const defaultTime = new Date(date);
          defaultTime.setHours(firstAvailableSlot.hour, 0, 0, 0);
          setSelectedStartTime(defaultTime);
        } else if (slots.length > 0) {
          // If no available slots, still set first slot as default but it will be disabled
          const defaultTime = new Date(date);
          defaultTime.setHours(slots[0].hour, 0, 0, 0);
          setSelectedStartTime(defaultTime);
        }
      } catch (err) {
        console.error('Error initializing time slots:', err);
        setError('Failed to load available time slots. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    initializeTimeSlots();
    fetchPricingRules();
  }, [date, vesselType, availableVessels]);
  
  // Fetch pricing rules
  const fetchPricingRules = async () => {
    try {
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('Fetching pricing rules for vessel type:', vesselType);
      // }
      const rules = await getPricingRules();
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('Fetched pricing rules:', rules);
      // }
      setPricingRules(rules);
    } catch (error) {
      console.error('Error fetching pricing rules:', error);
    }
  };
  
  // Check if a specific hour is available
  const checkHourAvailability = async (hour: number): Promise<boolean> => {
    if (!availableVessels.length) return false;
    
    try {
      const dateString = format(date, 'yyyy-MM-dd');
      const timeSlot = `${hour.toString().padStart(2, '0')}:00-${(hour + 1).toString().padStart(2, '0')}:00`;
      
      // Check if at least one vessel is available for this time slot
      for (const vessel of availableVessels) {
        const isAvailable = await checkAvailability(vessel.id, dateString, timeSlot as TimeSlot);
        if (isAvailable) return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error checking hour availability:', err);
      return false;
    }
  };
  
  // Handle time selection
  const handleTimeSelect = (hour: number) => {
    const newStartTime = new Date(date);
    newStartTime.setHours(hour, 0, 0, 0);
    setSelectedStartTime(newStartTime);
  };
  
  // Handle duration change
  const handleDurationChange = (event: SelectChangeEvent) => {
    setSelectedDuration(event.target.value);
  };
  
  // Calculate price based on duration
  const calculatePrice = (duration: string | number): number => {
    const durationNumber = typeof duration === 'string' ? parseInt(duration) : duration;
    
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('=== PRICING CALCULATION (HourlyTimeSlotSelector) ===');
    //   console.log('Calculating price for duration:', durationNumber, 'vessel type:', vesselType);
    //   console.log('Selected date:', date);
    //   console.log('Available pricing rules:', pricingRules);
    // }
    
    if (!pricingRules.length) {
      // if (process.env.NODE_ENV === 'development') {
      //   console.warn('No pricing rules available, using fallback');
      // }
      const defaultBaseRate = marinaSettings?.reservationRules?.baseRate || 100;
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('Fallback base rate from marina settings:', defaultBaseRate);
      // }
      return defaultBaseRate * durationNumber;
    }
    
    // Find the applicable pricing rule for this vessel type
    const applicableRule = pricingRules.find(rule => {
      const vesselTypeMatch = rule.vesselType === vesselType;
      const startDateMatch = !rule.startDate || new Date(rule.startDate) <= date;
      const endDateMatch = !rule.endDate || new Date(rule.endDate) >= date;
      
      return vesselTypeMatch && startDateMatch && endDateMatch;
    });
    
    if (!applicableRule) {
      // if (process.env.NODE_ENV === 'development') {
      //   console.warn('No applicable pricing rule found for vessel type:', vesselType);
      // }
      // if (process.env.NODE_ENV === 'development') {
      //   console.warn('Using fallback pricing');
      // }
      const defaultBaseRate = marinaSettings?.reservationRules?.baseRate || 100;
      return defaultBaseRate * durationNumber;
    }
    
    const basePrice = applicableRule.basePrice || 0;
    const timeBlockPrices = applicableRule.timeBlockPrices || [];
    
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('Found applicable rule:', applicableRule);
    //   console.log('Time block prices:', timeBlockPrices);
    // }
    
    // Find the applicable time block for this duration
    const applicableTimeBlock = timeBlockPrices
      .sort((a, b) => b.hours - a.hours) // Sort in descending order
      .find(block => durationNumber >= block.hours);
    
    if (!applicableTimeBlock) {
      // if (process.env.NODE_ENV === 'development') {
      //   console.warn('No applicable time block found for duration:', durationNumber);
      // }
      // if (process.env.NODE_ENV === 'development') {
      //   console.warn('Available time blocks:', timeBlockPrices);
      // }
      // If no time block matches, calculate based on base price
      return basePrice * durationNumber;
    }
    
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('Using time block:', applicableTimeBlock);
    //   console.log('Final price:', applicableTimeBlock.price);
    // }
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('=== END PRICING CALCULATION ===');
    // }
    
    return applicableTimeBlock.price;
  };
  
  // Handle confirm selection
  const handleConfirmSelection = () => {
    if (!selectedStartTime) {
      setError('Please select a start time');
      return;
    }
    
    // Create a custom time slot with the format "HH:MM-HH:MM"
    const startHour = selectedStartTime.getHours();
    const endHour = startHour + parseInt(selectedDuration);
    
    const timeSlot = `${startHour.toString().padStart(2, '0')}:00-${endHour.toString().padStart(2, '0')}:00` as TimeSlot;
    const price = calculatePrice(selectedDuration);
    
    // Call the parent component callback
    onTimeSlotSelect(timeSlot, price, parseInt(selectedDuration));
  };
  
  // Calculate end time based on start time and duration
  const calculateEndTime = (): Date | null => {
    if (!selectedStartTime) return null;
    return addHours(selectedStartTime, parseInt(selectedDuration));
  };
  
  // Format a time for display (12-hour format)
  const formatDisplayTime = (hour: number, minute: number = 0): string => {
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${suffix}`;
  };
  
  // Format business hours for display
  const getFormattedBusinessHours = (): string => {
    const dayOfWeek = format(date, 'EEEE').toLowerCase();
    
    if (!marinaSettings) {
      return 'Business hours not available';
    }
    
    // Check if the date is in season
    let isInSeason = true;
    if (marinaSettings.isSeasonalHoursEnabled && 
        marinaSettings.seasonStartDate && 
        marinaSettings.seasonEndDate) {
      
      try {
        // Use year from the selected date
        const yearToUse = date.getFullYear();
        
        // Parse season start date
        let seasonStart = null;
        if (marinaSettings.seasonStartDate.includes('-')) {
          const parts = marinaSettings.seasonStartDate.split('-');
          if (parts.length === 2) {
            // Format MM-DD, add year
            const [month, day] = parts.map(Number);
            seasonStart = new Date(yearToUse, month - 1, day);
          } else if (parts.length === 3) {
            // Format YYYY-MM-DD
            seasonStart = new Date(marinaSettings.seasonStartDate);
            // Update year if needed
            seasonStart.setFullYear(yearToUse);
          }
        }
        
        // Parse season end date
        let seasonEnd = null;
        if (marinaSettings.seasonEndDate.includes('-')) {
          const parts = marinaSettings.seasonEndDate.split('-');
          if (parts.length === 2) {
            // Format MM-DD, add year
            const [month, day] = parts.map(Number);
            seasonEnd = new Date(yearToUse, month - 1, day);
          } else if (parts.length === 3) {
            // Format YYYY-MM-DD
            seasonEnd = new Date(marinaSettings.seasonEndDate);
            // Update year if needed
            seasonEnd.setFullYear(yearToUse);
          }
        }
        
        // Check if the selected date is within season
        if (seasonStart && seasonEnd) {
          isInSeason = date >= seasonStart && date <= seasonEnd;
        }
      } catch (err) {
        console.error('Error parsing seasonal dates:', err);
        // Default to in-season if there's an error
        isInSeason = true;
      }
    }
    
    // Get the appropriate business hours based on season
    const businessHours = isInSeason && marinaSettings.inSeasonHours 
      ? marinaSettings.inSeasonHours
      : (!isInSeason && marinaSettings.outOfSeasonHours 
        ? marinaSettings.outOfSeasonHours
        : marinaSettings.businessHours);
    
    // Get hours for the selected day of week
    if (businessHours && businessHours[dayOfWeek]) {
      const dayHours = businessHours[dayOfWeek];
      
      // Check if the marina is open on this day
      if (!dayHours.isClosed && dayHours.openTime && dayHours.closeTime) {
        const openHour = parseInt(dayHours.openTime.split(':')[0]);
        const openMinute = parseInt(dayHours.openTime.split(':')[1]);
        const closeHour = parseInt(dayHours.closeTime.split(':')[0]);
        const closeMinute = parseInt(dayHours.closeTime.split(':')[1]);
        
        return `${formatDisplayTime(openHour, openMinute)} - ${formatDisplayTime(closeHour, closeMinute)}`;
      } else if (dayHours.isClosed) {
        return 'Closed';
      }
    }
    
    return 'Hours not specified';
  };
  
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Select Time for {format(date, 'MMMM d, yyyy')}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Available Starting Times:
          </Typography>
          
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {hourlySlots.map(({ hour, available }) => (
                              <Grid
                  key={hour}
                  xs={6}
                  sm={4}
                  md={3}>
                <Button
                  fullWidth
                  variant={selectedStartTime?.getHours() === hour ? "contained" : "outlined"}
                  color={available ? "success" : "error"}
                  onClick={() => available && handleTimeSelect(hour)}
                  disabled={!available}
                  sx={{ height: '48px' }}
                >
                  {formatDisplayTime(hour)}
                </Button>
              </Grid>
            ))}
          </Grid>
          
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid
              xs={12}
              sm={6}>
              <FormControl fullWidth>
                <InputLabel id="duration-label">Rental Duration (Hours)</InputLabel>
                <Select
                  labelId="duration-label"
                  id="duration-select"
                  value={selectedDuration}
                  label="Rental Duration (Hours)"
                  onChange={handleDurationChange}
                  disabled={!selectedStartTime}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(hours => {
                    const price = calculatePrice(hours);
                    return (
                      <MenuItem key={hours} value={hours.toString()}>
                        {hours} {hours === 1 ? 'hour' : 'hours'} - ${price.toFixed(2)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {selectedStartTime && (
            <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'white' }}>
              <Typography variant="h6" gutterBottom>
                Reservation Summary
              </Typography>
              <Typography variant="body1">
                Date: {format(date, 'EEEE, MMMM d, yyyy')}
              </Typography>
              <Typography variant="body1">
                Time: {formatDisplayTime(selectedStartTime.getHours())} - {calculateEndTime() ? formatDisplayTime(calculateEndTime()!.getHours()) : ''}
              </Typography>
              <Typography variant="body1">
                Duration: {selectedDuration} {selectedDuration === '1' ? 'hour' : 'hours'}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1 }}>
                Estimated Price: ${calculatePrice(selectedDuration).toFixed(2)} +Fuel and Tax
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                Today's Business Hours: {getFormattedBusinessHours()}
              </Typography>
            </Paper>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={onBack}>
              Back to Calendar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmSelection}
              disabled={!selectedStartTime}
            >
              Confirm Selection
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

// Define steps for the reservation process
const steps = ['Select Date & Time', 'Customer Information & Payment'];

const ReservationPage: React.FC = () => {
  const { vesselId } = useParams<{ vesselId: string }>();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { marinaSettings } = useSettings();
  
  // State for the reservation process
  const [activeStep, setActiveStep] = useState(0);
  const [vessel, setVessel] = useState<Vessel | null>(null);
  const [availableVessels, setAvailableVessels] = useState<Vessel[]>([]);
  const [vesselTypes, setVesselTypes] = useState<string[]>([]);
  const [selectedVesselType, setSelectedVesselType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reservationData, setReservationData] = useState<any>(null);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [confirmedVesselId, setConfirmedVesselId] = useState<string | null>(null);
  
  // State for reservation form
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [timeSlot, setTimeSlot] = useState<TimeSlot>('3');
  const [startTime, setStartTime] = useState('9:00 AM');
  const [endTime, setEndTime] = useState('12:00 PM');
  const [duration, setDuration] = useState('3');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [partySize, setPartySize] = useState(1);
  const [notes, setNotes] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [settings, setSettings] = useState<MarinaSetting | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDetails, setPromoCodeDetails] = useState<any>(null);
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const [checkingPromoCode, setCheckingPromoCode] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  
  // Customer selection state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  
  // Load vessel data and prefill form if returning from login
  useEffect(() => {
    const fetchVesselAndPrefillData = async () => {
      try {
        setLoading(true);
        
        // Check if we have vessel type and available vessels from the modal
        const state = location.state as { vesselType?: string; availableVessels?: Vessel[] } | null;
        if (state?.vesselType && state?.availableVessels) {
          setSelectedVesselType(state.vesselType);
          setAvailableVessels(state.availableVessels);
          
          // If a specific vessel is selected, use that
          if (vesselId) {
            const selectedVessel = state.availableVessels.find(v => v.id === vesselId);
            if (selectedVessel) {
              setVessel(selectedVessel);
              setTotalPrice(marinaSettings?.reservationRules?.baseRate || 0);
            }
          }
        } else if (vesselId) {
          // If no vessel type but we have a vesselId, fetch that specific vessel
          const vesselData = await getVesselById(vesselId);
          if (vesselData) {
            setVessel(vesselData);
            setSelectedVesselType(vesselData.type);
            setTotalPrice(marinaSettings?.reservationRules?.baseRate || 0);
            
            // Also fetch all vessels of this type
            const vesselsData = await getVesselsByType();
            if (vesselData.type && vesselsData[vesselData.type]) {
              setAvailableVessels(vesselsData[vesselData.type]);
            }
          }
        } else {
          // If no vessel type or ID is provided, fetch all vessel types for selection
          const vesselsData = await getVesselsByType();
          const types = Object.keys(vesselsData);
          setVesselTypes(types);
          
          if (types.length > 0) {
            const firstType = types[0];
            setSelectedVesselType(firstType);
            
            // Get vessels of the first type
            if (vesselsData[firstType]) {
              setAvailableVessels(vesselsData[firstType]);
            }
          }
        }
        
        // Prefill form with user data if available
        if (userProfile) {
          setFirstName(userProfile.firstName);
          setLastName(userProfile.lastName);
          setEmail(userProfile.email);
          setPhone(userProfile.phone);
          
          // Try to find existing customer by email
          try {
            const existingCustomers = await searchCustomers(userProfile.email);
            const matchingCustomer = existingCustomers.find(
              c => c.email.toLowerCase() === userProfile.email.toLowerCase()
            );
            if (matchingCustomer) {
              setSelectedCustomer(matchingCustomer);
            }
          } catch (err) {
            console.log('Could not search for existing customer:', err);
          }
        } else if (currentUser) {
          setEmail(currentUser.email || '');
          
          // Try to find existing customer by email
          if (currentUser.email) {
            try {
              const existingCustomers = await searchCustomers(currentUser.email);
              const matchingCustomer = existingCustomers.find(
                c => c.email.toLowerCase() === currentUser.email!.toLowerCase()
              );
              if (matchingCustomer) {
                setSelectedCustomer(matchingCustomer);
                setFirstName(matchingCustomer.firstName);
                setLastName(matchingCustomer.lastName);
                setPhone(matchingCustomer.phone);
              }
            } catch (err) {
              console.log('Could not search for existing customer:', err);
            }
          }
        }
        
        // Check if there's a pending reservation in sessionStorage
        const pendingReservationJson = sessionStorage.getItem('pendingReservation');
        if (pendingReservationJson) {
          try {
            const pendingReservation = JSON.parse(pendingReservationJson);
            
            // Only prefill if the vessel ID matches
            if (pendingReservation.vesselId === vesselId) {
              // Fix for off-by-one: parse YYYY-MM-DD safely
              const dateStr = pendingReservation.date;
              let parsedDate;
              if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                const [year, month, day] = dateStr.split('-').map(Number);
                parsedDate = new Date(year, month - 1, day);
              } else {
                parsedDate = new Date(dateStr);
              }
              setSelectedDate(parsedDate);
              
              // Set the time slot
              if (pendingReservation.timeSlot) {
                setTimeSlot(pendingReservation.timeSlot);
              }
              
              // Set customer details
              if (pendingReservation.customerDetails) {
                const { firstName, lastName, email, phone } = pendingReservation.customerDetails;
                setFirstName(firstName || '');
                setLastName(lastName || '');
                setEmail(email || '');
                setPhone(phone || '');
              }
              
              // Set notes
              if (pendingReservation.notes) {
                setNotes(pendingReservation.notes);
              }
              
              // Clear the pending reservation from sessionStorage
              sessionStorage.removeItem('pendingReservation');
              
              // Move to the review step
              setActiveStep(1);
            }
          } catch (err) {
            console.error('Error parsing pending reservation:', err);
          }
        }
      } catch (err) {
        console.error('Error fetching vessel:', err);
        setError('Failed to load vessel information');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVesselAndPrefillData();
  }, [vesselId, userProfile, currentUser, location.state, marinaSettings]);
  
  // Handle vessel type change
  const handleVesselTypeChange = async (type: string) => {
    setSelectedVesselType(type);
    setLoading(true);
    
    try {
      const vesselsData = await getVesselsByType();
      if (vesselsData[type]) {
        setAvailableVessels(vesselsData[type]);
      } else {
        setAvailableVessels([]);
      }
    } catch (err) {
      console.error(`Error loading vessels of type ${type}:`, err);
      setError(`Failed to load vessels of type ${type}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Updates for time slot and rate calculations
  useEffect(() => {
    if (!vessel) return;
    
    // Set times based on selected time slot
    if (timeSlot === '3') {
      setStartTime('9:00 AM');
      setEndTime('12:00 PM');
      setDuration('3');
      setTotalPrice(marinaSettings?.reservationRules?.baseRate || 0);
    } else if (timeSlot === '7') {
      setStartTime('9:00 AM');
      setEndTime('4:00 PM');
      setDuration('7');
      setTotalPrice((marinaSettings?.reservationRules?.baseRate || 0) * 2);
    } else if (timeSlot.includes('-')) {
      // Parse custom time slot format "HH:MM-HH:MM"
      const [start, end] = timeSlot.split('-');
      
      // Convert from 24-hour format to 12-hour format
      const startHour = parseInt(start.split(':')[0]);
      const startMinute = parseInt(start.split(':')[1]);
      const endHour = parseInt(end.split(':')[0]);
      const endMinute = parseInt(end.split(':')[1]);
      
      // Format for display
      const startPeriod = startHour >= 12 ? 'PM' : 'AM';
      const endPeriod = endHour >= 12 ? 'PM' : 'AM';
      
      const displayStartHour = startHour % 12 || 12;
      const displayEndHour = endHour % 12 || 12;
      
      setStartTime(`${displayStartHour}:${startMinute.toString().padStart(2, '0')} ${startPeriod}`);
      setEndTime(`${displayEndHour}:${endMinute.toString().padStart(2, '0')} ${endPeriod}`);
      
      // Calculate duration in hours
      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;
      const durationHours = (endTimeInMinutes - startTimeInMinutes) / 60;
      
      setDuration(durationHours.toString());
    }
    
    // Reset availability check when time slot changes
    setIsAvailable(null);
  }, [timeSlot, vessel, marinaSettings]);
  
  // Handle date selection from calendar
  const handleDateSelect = (date: Date) => {
    console.log('Date selected:', date);
    setSelectedDate(date);
    setShowTimeSlots(true);
  };
  
  // Handle time slot selection from calendar or hourly selector
  const handleTimeSlotSelect = (selectedTimeSlot: TimeSlot, price?: number, duration?: string | number) => {
    console.log('Time slot selected:', selectedTimeSlot, 'price:', price, 'duration:', duration);
    
    if (!selectedDate) {
      setError('Please select a date first');
      return;
    }
    
    setTimeSlot(selectedTimeSlot);
    setShowTimeSlots(false);
    setError(null);
    
    // Set the total price if provided
    if (typeof price === 'number') {
      setTotalPrice(price);
    }
    
    // Set duration if provided
    if (duration) {
      setDuration(duration.toString());
    }
    
    checkAndConfirmAvailableVessel(selectedDate, selectedTimeSlot);
  };

  // Handler for multi-day time slot selector
  const handleMultiDayTimeSlotSelect = (date: Date, selectedTimeSlot: TimeSlot, price?: number) => {
    console.log('Multi-day time slot selected:', date, selectedTimeSlot, 'price:', price);
    
    // Update the selected date if different
    if (!selectedDate || selectedDate.getTime() !== date.getTime()) {
      setSelectedDate(date);
    }
    
    setTimeSlot(selectedTimeSlot);
    setShowTimeSlots(false);
    setError(null);
    
    // Parse the time slot to set start and end times
    if (selectedTimeSlot.includes('-')) {
      const [start, end] = selectedTimeSlot.split('-');
      const startHour = parseInt(start.split(':')[0]);
      const endHour = parseInt(end.split(':')[0]);
      
      const startPeriod = startHour >= 12 ? 'PM' : 'AM';
      const endPeriod = endHour >= 12 ? 'PM' : 'AM';
      
      const formattedStart = `${startHour % 12 || 12}:00 ${startPeriod}`;
      const formattedEnd = `${endHour % 12 || 12}:00 ${endPeriod}`;
      
      setStartTime(formattedStart);
      setEndTime(formattedEnd);
      setDuration((endHour - startHour).toString());
    }
    
    // Set the price if provided
    if (typeof price === 'number') {
      setTotalPrice(price);
    }
    
    checkAndConfirmAvailableVessel(date, selectedTimeSlot);
  };

  // Handler for date change from multi-day selector
  const handleDateChangeFromMultiDay = (date: Date) => {
    setSelectedDate(date);
    setShowTimeSlots(false);
    setTimeSlot(null);
    setStartTime('');
    setEndTime('');
    setDuration('');
    setTotalPrice(0);
    setConfirmedVesselId(null);
    sessionStorage.removeItem('confirmedAvailableVessel');
  };
  
  // Check and confirm available vessel
  const checkAndConfirmAvailableVessel = async (date: Date, timeSlot: TimeSlot) => {
    if (!date) {
      console.error('Invalid date provided to checkAndConfirmAvailableVessel');
      setError('Invalid date selected. Please try again.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const dateString = format(date, 'yyyy-MM-dd');
      console.log(`Validating availability for ${vessel?.type} on ${dateString} at time slot ${timeSlot}`);
      
      // Validate availability without assigning specific vessels
      // This will be done during check-in instead
      setVessel(vessel);
      setConfirmedVesselId(null); // No vessel confirmed during booking
      
      setIsAvailable(true);
      setActiveStep(1); // Move to the next step
    } catch (err) {
      console.error('Error validating vessel availability:', err);
      setError('Failed to validate availability. Please try again.');
      setIsAvailable(false);
      setConfirmedVesselId(null);
      
      // Keep the calendar visible
      setShowTimeSlots(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Form input handlers
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhoneNumber(e.target.value));
  };
  
  // Customer selection handlers
  const handleCustomerSelect = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      setFirstName(customer.firstName);
      setLastName(customer.lastName);
      setEmail(customer.email);
      setPhone(customer.phone);
    } else {
      // Clear form if no customer selected
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
    }
  };

  const handleCustomerCreate = async (customerData: CustomerInput) => {
    try {
      const newCustomer = await createCustomer(customerData);
      // Select the newly created customer
      handleCustomerSelect(newCustomer);
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error; // Re-throw so CustomerSelector can handle the error
    }
  };
  
  // Step navigation
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  // Update final price whenever totalPrice or discountAmount changes
  useEffect(() => {
    setFinalPrice(Math.max(0, totalPrice - discountAmount));
  }, [totalPrice, discountAmount]);
  
  // Handle promo code validation
  const handlePromoCodeValidation = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError('Please enter a promo code');
      return;
    }
    
    if (!selectedDate || !vessel) {
      setPromoCodeError('Please select a date and vessel first');
      return;
    }
    
    setCheckingPromoCode(true);
    setPromoCodeError(null);
    setPromoCodeApplied(false);
    
    try {
      // Call the discount service to validate the promo code
      // Try to calculate duration and hourly rate for duration-based discounts
      let durationMinutes: number | undefined;
      let hourlyRate: number | undefined;

      if (duration) {
        const durationNumber = parseFloat(duration);
        if (durationNumber > 0) {
          durationMinutes = durationNumber * 60;
          hourlyRate = totalPrice / durationNumber;
          
          console.log('Calculated duration for promo validation:', {
            duration,
            durationNumber,
            durationMinutes,
            hourlyRate,
            totalPrice
          });
        }
      }
      
      const result = await validatePromoCode(
        promoCode,
        currentUser?.id || '',
        totalPrice,
        vessel.type,
        selectedDate,
        durationMinutes,
        hourlyRate
      );
      
      if ('error' in result) {
        setPromoCodeError(result.error);
        setDiscountAmount(0);
        return;
      }
      
      // Store the discount information
      setPromoCodeDetails(result.discountDetails);
      setPromoCodeApplied(true);
      
      // Apply the discount
      setDiscountAmount(result.discount);
      
      // Clear the error
      setPromoCodeError(null);
    } catch (err) {
      console.error('Error validating promo code:', err);
      setPromoCodeError('Failed to validate promo code');
      setDiscountAmount(0);
    } finally {
      setCheckingPromoCode(false);
    }
  };
  
  // Clear promo code application
  const handleClearPromoCode = () => {
    setPromoCode('');
    setPromoCodeApplied(false);
    setPromoCodeDetails(null);
    setPromoCodeError(null);
    setDiscountAmount(0);
  };
  
  // Submit reservation (update to include promo code and discount)
  const handleSubmit = async () => {
    if (!vessel || !selectedDate) {
      setError('Missing required information');
      return;
    }
    
    // Validate that customer is selected
    if (!selectedCustomer) {
      setError('Please select or create a customer before proceeding');
      return;
    }
    
    // Validate required customer fields
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim()) {
      setError('Please ensure all customer information is complete');
      return;
    }
    
    try {
      setLoading(true);
      
      const dateString = format(selectedDate, 'yyyy-MM-dd');
      
      // Get the confirmed vessel data from session storage
      const confirmedVesselJson = sessionStorage.getItem('confirmedAvailableVessel');
      let confirmedVesselData = null;
      
      if (confirmedVesselJson) {
        confirmedVesselData = JSON.parse(confirmedVesselJson);
        
        // Check if the confirmation is expired (30 minutes)
        const now = new Date().getTime();
        const confirmationTime = confirmedVesselData.timestamp;
        const expirationTime = 30 * 60 * 1000; // 30 minutes in milliseconds
        
        if (now - confirmationTime > expirationTime) {
          // Confirmation expired, clear it and show error
          sessionStorage.removeItem('confirmedAvailableVessel');
          throw new Error('Your reservation confirmation has expired. Please check availability again.');
        }
        
        // Check if the confirmed vessel matches the current selection
        if (confirmedVesselData.date !== dateString || 
            confirmedVesselData.timeSlot !== timeSlot) {
          throw new Error('The date or time selected no longer matches what was confirmed. Please check availability again.');
        }
      } else {
        // No confirmation found, need to check availability again
        throw new Error('Please check availability before confirming your reservation.');
      }
      
      // Create the reservation data object
      const reservationData = {
        customerId: selectedCustomer?.id || currentUser?.id || 'staff-created',
        customerDetails: {
          firstName,
          lastName,
          email,
          phone
        },
        vesselId: '', // No vessel assigned during booking - will be assigned at check-in
        vesselName: `${vessel.type} (TBD)`,
        vesselType: vessel.type,
        partySize,
        date: dateString,
        timeSlot,
        startTime,
        endTime,
        totalPrice: finalPrice, // Use final price after discounts
        originalPrice: totalPrice, // Store original price
        discountAmount: discountAmount, // Store discount amount
        discountId: promoCodeDetails?.id || null, // Store the discount ID if applicable
        discountType: promoCodeApplied ? 'promo' : null, // Store the discount type
        promoCode: promoCodeApplied ? promoCode : null, // Store the promo code if applied
        notes,
        status: 'pending',
        createdBy: 'staff'
      };
      
      // Store the reservation data in state for the payment component
      setReservationData(reservationData);
      
    } catch (err) {
      console.error('Error preparing reservation:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to prepare reservation');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle reservation completion
  const handleReservationComplete = (reservationId: string) => {
    // Navigate to staff reservations page
    navigate('/staff/reservations', { 
      state: { 
        reservationSuccess: true,
        reservationId: reservationId
      } 
    });
  };
  
  // Handle reservation cancellation
  const handleReservationCancel = () => {
    setActiveStep(0); // Go back to the first step
  };
  
  const handlePaymentError = (error: string) => {
    setError(error);
    setActiveStep(0); // Go back to the first step
  };
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const marinaSettings = await getMarinaSettings();
        setSettings(marinaSettings);
      } catch (error) {
        console.error('Error fetching marina settings:', error);
      }
    };
    
    fetchSettings();
  }, []);
  
  // Handle going back to the date selection
  const handleBackToDateSelection = () => {
    setShowTimeSlots(false);
  };
  
  // Utility function to capitalize each word
  const capitalizeWords = (str: string) =>
    str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  
  // Render the form based on active step
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Date and Time
            </Typography>
            
            {/* Vessel type selection if no vessel is selected yet */}
            {!vessel && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="vessel-type-label">Vessel Type</InputLabel>
                <Select
                  labelId="vessel-type-label"
                  id="vessel-type"
                  value={selectedVesselType}
                  label="Vessel Type"
                  onChange={(e) => handleVesselTypeChange(e.target.value)}
                >
                  {vesselTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {/* Show either the calendar or multi-day time slot selector */}
            {selectedVesselType && !selectedDate && (
              <AvailabilityCalendar
                vesselType={selectedVesselType}
                onDateSelect={handleDateSelect}
                onTimeSlotSelect={handleTimeSlotSelect}
              />
            )}
            
            {/* Show multi-day time slot selector when a date is selected */}
            {selectedDate && showTimeSlots && (
              <MultiDayTimeSlotSelector
                selectedDate={selectedDate}
                vesselType={selectedVesselType}
                availableVessels={availableVessels}
                selectedDuration={3}
                onTimeSlotSelect={handleMultiDayTimeSlotSelect}
                onDateChange={handleDateChangeFromMultiDay}
              />
            )}
            
            {/* Selected date and time summary - only show when not showing the calendar time slots and a date is selected */}
            {selectedDate && !showTimeSlots && confirmedVesselId && (
              <Box 
                sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}
                id="reservation-summary"
              >
                <Typography variant="subtitle1" gutterBottom>
                  Selected Date: {format(selectedDate, 'MMMM d, yyyy')}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Selected Time: {startTime} - {endTime}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Duration: {`${duration} hours`}
                </Typography>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Reservation Total: ${totalPrice.toFixed(2)} +Fuel and Tax
                </Typography>
              </Box>
            )}
            
            {/* Back to Calendar button - only show when not showing the calendar time slots and we have a confirmed vessel */}
            {selectedDate && !showTimeSlots && confirmedVesselId && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    setShowTimeSlots(true);
                    setConfirmedVesselId(null);
                  }}
                >
                  Change Time Selection
                </Button>
              </Box>
            )}
          </Box>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid xs={12} md={7}>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <CustomerSelector
                onCustomerSelect={handleCustomerSelect}
                onCustomerCreate={handleCustomerCreate}
                selectedCustomer={selectedCustomer}
              />
              
              {/* Additional reservation fields */}
              {selectedCustomer && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Party Size"
                      type="number"
                      variant="outlined"
                      value={partySize}
                      onChange={(e) => setPartySize(parseInt(e.target.value))}
                      InputProps={{ 
                        inputProps: { 
                          min: 1, 
                          max: vessel?.capacity || 10
                        } 
                      }}
                      required
                    />
                  </Grid>
                  <Grid xs={12}>
                    <TextField
                      fullWidth
                      label="Special Requests / Notes"
                      multiline
                      rows={3}
                      variant="outlined"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Grid>
                </Grid>
              )}
              
              {/* Add Promo Code section */}
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Promo Code
                </Typography>
                                  <Grid container spacing={2} alignItems="center">
                    <Grid xs>
                      <TextField
                      fullWidth
                      label="Promo Code"
                      variant="outlined"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      disabled={promoCodeApplied}
                    />
                  </Grid>
                  <Grid>
                    {!promoCodeApplied ? (
                      <Button
                        variant="outlined"
                        onClick={handlePromoCodeValidation}
                        disabled={!promoCode.trim() || checkingPromoCode}
                      >
                        {checkingPromoCode ? <CircularProgress size={24} /> : 'Apply'}
                      </Button>
                    ) : (
                      <IconButton color="error" onClick={handleClearPromoCode}>
                        <CancelIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
                
                {promoCodeError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {promoCodeError}
                  </Alert>
                )}
                
                {promoCodeApplied && promoCodeDetails && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Promo code applied! You're saving ${discountAmount.toFixed(2)}
                  </Alert>
                )}
              </Box>
            </Grid>
            <Grid xs={12} md={5}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Reservation Summary
                  </Typography>
                  <List disablePadding>
                    <ListItem>
                      <ListItemText 
                        primary="Vessel" 
                        secondary={vessel?.name} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Vessel Type" 
                        secondary={vessel ? capitalizeWords(vessel.type) : ''} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Date" 
                        secondary={selectedDate?.toLocaleDateString()} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Time" 
                        secondary={`${startTime} - ${endTime}`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Duration" 
                        secondary={`${duration} hours`} 
                      />
                    </ListItem>
                    <Divider sx={{ my: 1 }} />
                    <ListItem>
                      <ListItemText primary="Base Price" />
                      <Typography variant="body2">${totalPrice.toFixed(2)}</Typography>
                    </ListItem>
                    {discountAmount > 0 && (
                      <ListItem>
                        <ListItemText primary="Discount" secondary={promoCodeDetails?.code} />
                        <Typography variant="body2" color="error.main">
                          -${discountAmount.toFixed(2)}
                        </Typography>
                      </ListItem>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <ListItem>
                      <ListItemText primary="Total Price" />
                      <Typography variant="h6">${finalPrice.toFixed(2)}</Typography>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Make a Reservation
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <React.Fragment>
            {renderStepContent(activeStep)}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>
              {activeStep === 1 && !reservationData && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={!selectedCustomer}
                >
                  Proceed to Payment
                </Button>
              )}
            </Box>
          </React.Fragment>
        )}
      </Paper>
      
      {activeStep === 1 && reservationData && (
        <ReservationPayment 
          reservationData={reservationData}
          onReservationComplete={handleReservationComplete}
          onCancel={handleReservationCancel}
        />
      )}
    </Container>
  );
};

export default ReservationPage; 